/**
 * A search by someone who already owns photos here, split into what is new and
 * what they have.
 *
 * The finder used to treat every search as a first visit: an athlete who had
 * bought a photo, searched again and matched that same photo was shown it
 * watermarked, with "Unlock photos" beneath, as if they had never paid. So
 * "your photos" and the search disagreed about what was theirs. Now a search
 * only ever offers what they do not have yet, and a search that finds nothing
 * new goes back to the photos they own.
 */
export interface OwnedSplit {
  /** Confident matches they do not own yet. */
  newMatched: string[];
  /** "Might also be you" photos they do not own yet. */
  newPossible: string[];
  /** How many of the photos found (confident or possible) they already own. */
  alreadyOwned: number;
  /** They own photos here, the search found some, and every one is owned. */
  nothingNew: boolean;
}

export function splitSearchByOwned(input: {
  matchedPhotoIds: string[];
  possibleIds: string[];
  ownedIds: string[];
}): OwnedSplit {
  const owned = new Set(input.ownedIds);
  const newMatched = input.matchedPhotoIds.filter((id) => !owned.has(id));
  const newPossible = input.possibleIds.filter((id) => !owned.has(id));
  const found = new Set([...input.matchedPhotoIds, ...input.possibleIds]);
  let alreadyOwned = 0;
  for (const id of found) if (owned.has(id)) alreadyOwned += 1;
  return {
    newMatched,
    newPossible,
    alreadyOwned,
    nothingNew: owned.size > 0 && alreadyOwned > 0 && newMatched.length === 0 && newPossible.length === 0,
  };
}

/** The line shown over a search that found some photos they already own. */
export function alreadyOwnedNote(alreadyOwned: number): string | null {
  if (alreadyOwned <= 0) return null;
  return alreadyOwned === 1
    ? 'One photo this search found is already yours, so it is not shown again here.'
    : `${alreadyOwned} photos this search found are already yours, so they are not shown again here.`;
}
